import { useEffect, useState, type ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Smartphone } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import { RacoreText } from '@/components/Logos';
import { isAllowedDevice as isAllowedDeviceCore } from '@/utils/device';

function isAllowedDevice(): boolean {
  if (typeof window === 'undefined') return true;
  return isAllowedDeviceCore({
    isNative: Capacitor.isNativePlatform(),
    coarsePointer: window.matchMedia?.('(pointer: coarse)')?.matches ?? false,
    width: window.innerWidth,
  });
}

/**
 * Blocks desktop/laptop browsers with a "use mobile/tablet" screen, so the
 * app-first experience is only served on the intended form factors.
 */
export default function DeviceGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState(isAllowedDevice);

  useEffect(() => {
    const check = () => setAllowed(isAllowedDevice());
    window.addEventListener('resize', check);
    const mq = window.matchMedia?.('(pointer: coarse)');
    mq?.addEventListener?.('change', check);
    return () => {
      window.removeEventListener('resize', check);
      mq?.removeEventListener?.('change', check);
    };
  }, []);

  if (allowed) return <>{children}</>;

  return (
    <div
      className="tw:flex tw:h-dvh tw:flex-col tw:items-center tw:justify-center tw:gap-6 tw:px-8 tw:text-center"
      data-testid="device-gate"
    >
      <RacoreText width={160} height={34} />
      <span className="tw:flex tw:size-20 tw:items-center tw:justify-center tw:rounded-3xl tw:bg-dark-1 tw:text-blue-primary">
        <Smartphone size={40} />
      </span>
      <h1 className="tw:heading-2 tw:text-white">{t('deviceGate.title')}</h1>
      <p className="tw:max-w-[360px] tw:text-base tw:text-grey-2">{t('deviceGate.description')}</p>
    </div>
  );
}
